// The one chart: Canvas 2D at device pixel ratio, lanes stacked top to bottom, each with its own y
// scale. Data draws on a base canvas; the time cursor and the hover readout draw on a second one,
// so following the clock repaints only a line unless the x window itself moves (an EEG page).

import { useEffect, useRef, useState } from 'react';
import { useAtlas } from '../app/store.ts';
import { clockTicks, decimate, formatTick, formatValue, invert, logTicks, niceTicks, placeLabels, readoutX, scale, type Scale, type SecondClock, type XAxis } from './chart.ts';

export interface Series {
  label: string;
  colour: string;
  x: ArrayLike<number>;
  y: ArrayLike<number>;
  /** `area` fills down to `base` (or the lane's floor): stacked areas pass the series below as base.
   *  `ticks` is a raster: each point a mark `width` wide and one y unit tall (a cell's row), at least a pixel. */
  style?: 'line' | 'dash' | 'area' | 'ticks';
  base?: ArrayLike<number>;
  width?: number;
  alpha?: number;
  /** Shown in the hover readout (default true). */
  readout?: boolean;
  /** The value the readout reports, when the drawn y isn't it (a stacked area's own layer). */
  readoutY?: ArrayLike<number>;
  unit?: string;
}

export interface Lane {
  /** Left label (a channel, or a quantity with its unit). */
  label?: string;
  unit?: string;
  y: [number, number];
  /** y tick values, or 'auto' for nice ticks; strips use none. */
  ticks?: number[] | 'auto' | 'none';
  /** Relative height. */
  weight?: number;
  series: Series[];
}

export interface ChartModel {
  x: XAxis & { domain: [number, number]; log?: boolean; ticks?: number[] };
  /** A second clock along the top, lined up with `x` (a model's own time over a recording's). */
  x2?: SecondClock;
  lanes: Lane[];
  /** Dotted lines with a label where it fits; lower ranks claim label space first. */
  events?: { x: number; label: string; rank?: number }[];
  bands?: { x0: number; x1: number; colour: string; label?: string }[];
  markers?: { x: number; colour: string; label?: string }[];
  /** A vertical scale bar for strips: `value` in lane y units, drawn at the right. */
  scaleBar?: { value: number; label: string };
  /** Point labels: a dot at (x, y) in a lane's units, with text beside it (a curve's Ki). */
  labels?: { lane: number; x: number; y: number; text: string; colour: string }[];
  /** Lane labels gutter width, CSS px (strips with channel names need more). */
  gutter?: number;
  /** The hover readout lists every lane's series (`all`, the default) or only the lane under the pointer (`lane`: a strip). */
  readout?: 'all' | 'lane';
}

export interface ChartProps {
  model: ChartModel;
  height: number;
  /** The x of the time cursor at scene time t, or null to hide it. */
  cursor?: (t: number) => number | null;
  /** Moves the x window with the clock (an EEG page that follows the cursor). */
  window?: (t: number) => [number, number];
  /** Click or drag on the plot: x in data units. */
  onSeek?: (x: number) => void;
  label: string;
}

const FONT = '500 11px "Inter Variable", Inter, system-ui, sans-serif';
/** Tick values and a strip's lane labels. */
export const MONO = '500 11px "JetBrains Mono Variable", "JetBrains Mono", ui-monospace, monospace';
const C = { grid: 'rgba(255,255,255,0.07)', axis: 'rgba(255,255,255,0.14)', text: '#a7b0be', faint: '#6f7a8a', cursor: '#eef2f7', event: 'rgba(255,255,255,0.32)' };
/** The traces panel's surface (`--ba-color-surface-translucent-panel`), as a halo round text that sits on lines. */
const HALO = 'rgba(17, 21, 29, 0.9)';

/** Text with a halo of the panel's surface, so a line it sits on breaks round it rather than striking it through. */
function haloText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
  ctx.save();
  ctx.strokeStyle = HALO;
  ctx.lineWidth = 4;
  ctx.lineJoin = 'round';
  ctx.strokeText(text, x, y);
  ctx.restore();
  ctx.fillText(text, x, y);
}
const PAD = { top: 16, right: 10, bottom: 18 };
/** The row a second clock's ticks take above the lanes. */
const CLOCK_ROW = 16;

let measurer: CanvasRenderingContext2D | null = null;
/** The width of a line of text in CSS px (the chart's 11 px label font by default). */
export function textWidth(text: string, font = FONT): number {
  measurer ??= document.createElement('canvas').getContext('2d');
  if (!measurer) return text.length * 6.5;
  measurer.font = font;
  return measurer.measureText(text).width;
}
/** A row of 11 px labels. */
const ROW = 14;

interface Layout {
  w: number;
  h: number;
  left: number;
  /** Where lanes start: below the label row (two rows when the labels in it would collide), and a second clock's. */
  plotTop: number;
  /** Where event lines start: up to their labels, through a second clock's row. */
  lineTop: number;
  x: Scale;
  lanes: { top: number; bottom: number; y: Scale; lane: Lane }[];
  /** Event and marker labels that fit, at their left edges; the rest keep only their line. */
  notes: { left: number; text: string; colour: string }[];
}

function laneTitle(lane: Lane | undefined): string | null {
  if (!lane?.label || lane.ticks === 'none') return null;
  return lane.unit ? `${lane.label} (${lane.unit})` : lane.label;
}

function layout(ctx: CanvasRenderingContext2D, model: ChartModel, w: number, h: number, domain: [number, number]): Layout {
  const left = model.gutter ?? 44;
  const right = w - PAD.right - (model.scaleBar ? 58 : 0);
  const x: Scale = { domain, range: [left, right], log: model.x.log };
  // Event and marker labels share the top row with the first lane's title. When one would land on
  // the title, the title moves down a row rather than either being lost.
  ctx.font = FONT;
  const notes = [...(model.events ?? []).map((e) => ({ x: e.x, text: e.label, colour: C.text, rank: e.rank ?? 0 })), ...(model.markers ?? []).flatMap((m) => (m.label ? [{ x: m.x, text: m.label, colour: m.colour, rank: 0 }] : []))]
    .map((n) => ({ ...n, px: scale(x, n.x), w: ctx.measureText(n.text).width }))
    .filter((n) => n.px >= left - 0.5 && n.px <= right + 0.5);
  const items = notes.map((n) => ({ x: n.px, w: n.w, rank: n.rank }));
  const title = laneTitle(model.lanes[0]);
  const free = placeLabels(items, left, right, { anchor: 'start' });
  const shared = title ? placeLabels(items, left, right, { anchor: 'start', taken: [[left, left + ctx.measureText(title).width]] }) : free;
  const split = shared.some((v, i) => v === null && free[i] !== null);
  const lefts = split ? free : shared;
  const notesTop = PAD.top + (split ? ROW : 0);
  const top = notesTop + (model.x2 ? CLOCK_ROW : 0);
  const bottom = h - PAD.bottom;
  const total = model.lanes.reduce((a, l) => a + (l.weight ?? 1), 0) || 1;
  // Lanes after the first carry their title in the gap above them, clear of the ticks below the one before.
  const gap = model.lanes.length > 1 && model.lanes.some((l) => l.ticks !== 'none') ? (model.lanes.slice(1).some((l) => laneTitle(l)) ? ROW + 8 : 10) : 0;
  const avail = bottom - top - gap * Math.max(0, model.lanes.length - 1);
  let y = top;
  const lanes = model.lanes.map((lane) => {
    const hh = (avail * (lane.weight ?? 1)) / total;
    const out = { top: y, bottom: y + hh, y: { domain: lane.y, range: [y + hh, y] as [number, number] }, lane };
    y += hh + gap;
    return out;
  });
  return { w, h, left, plotTop: top, lineTop: notesTop - 2, x, lanes, notes: notes.flatMap((n, i) => (lefts[i] === null ? [] : [{ left: lefts[i]!, text: n.text, colour: n.colour }])) };
}

function drawBase(ctx: CanvasRenderingContext2D, model: ChartModel, L: Layout) {
  ctx.clearRect(0, 0, L.w, L.h);
  const [x0, x1] = L.x.domain;
  const px = L.x.range[1] - L.x.range[0];
  const X = (v: number) => scale(L.x, v);
  // Bands under everything (an exposure range, a reperfusion window).
  for (const b of model.bands ?? []) {
    const a = Math.max(L.x.range[0], X(b.x0));
    const z = Math.min(L.x.range[1], X(b.x1));
    if (z <= a) continue;
    ctx.fillStyle = b.colour;
    ctx.fillRect(a, L.plotTop, Math.max(1, z - a), L.h - L.plotTop - PAD.bottom);
  }
  for (const { top, bottom, y, lane } of L.lanes) {
    const Y = (v: number) => scale(y, v);
    const ticks = lane.ticks === 'none' ? [] : Array.isArray(lane.ticks) ? lane.ticks : niceTicks(lane.y[0], lane.y[1], Math.max(2, Math.round((bottom - top) / 28)));
    ctx.font = MONO;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (const t of ticks) {
      const yy = Math.round(Y(t)) + 0.5;
      if (yy < top - 1 || yy > bottom + 1) continue;
      ctx.strokeStyle = t === 0 ? C.axis : C.grid;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(L.left, yy);
      ctx.lineTo(L.x.range[1], yy);
      ctx.stroke();
      ctx.fillStyle = C.faint;
      ctx.fillText(formatTick(t), L.left - 6, yy);
    }
    if (lane.label) {
      ctx.font = lane.ticks === 'none' ? MONO : FONT;
      ctx.fillStyle = C.text;
      ctx.textAlign = lane.ticks === 'none' ? 'right' : 'left';
      ctx.textBaseline = lane.ticks === 'none' ? 'middle' : 'alphabetic';
      if (lane.ticks === 'none') ctx.fillText(lane.label, L.left - 8, (top + bottom) / 2);
      else haloText(ctx, laneTitle(lane)!, L.left, top - 4);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(L.left, top - 2, px, bottom - top + 4);
    ctx.clip();
    for (const s of lane.series) {
      if (s.style === 'ticks') {
        // Every point, undecimated: a raster's spikes are its data.
        const rowPx = Math.max(1, Math.abs(Y(lane.y[0] + 1) - Y(lane.y[0])));
        const w = s.width ?? 1.5;
        ctx.globalAlpha = s.alpha ?? 1;
        ctx.fillStyle = s.colour;
        for (let i = 0; i < s.x.length; i++) {
          const xv = s.x[i]!;
          if (xv < x0 || xv > x1) continue;
          ctx.fillRect(X(xv) - w / 2, Y(s.y[i]! + 1), w, rowPx);
        }
        continue;
      }
      const d = decimate(s.x, s.y, x0, x1, px, model.x.log ? (v) => X(v) - L.left : undefined);
      if (!d.x.length) continue;
      ctx.globalAlpha = s.alpha ?? 1;
      if (s.style === 'area') {
        const base = s.base ? decimate(s.x, s.base, x0, x1, px, model.x.log ? (v) => X(v) - L.left : undefined) : null;
        ctx.beginPath();
        d.x.forEach((xv, i) => (i ? ctx.lineTo(X(xv), Y(d.y[i]!)) : ctx.moveTo(X(xv), Y(d.y[i]!))));
        if (base && base.x.length === d.x.length) for (let i = base.x.length - 1; i >= 0; i--) ctx.lineTo(X(base.x[i]!), Y(base.y[i]!));
        else {
          ctx.lineTo(X(d.x.at(-1)!), Y(Math.max(lane.y[0], 0)));
          ctx.lineTo(X(d.x[0]!), Y(Math.max(lane.y[0], 0)));
        }
        ctx.closePath();
        ctx.fillStyle = s.colour;
        ctx.fill();
        continue;
      }
      ctx.strokeStyle = s.colour;
      ctx.lineWidth = s.width ?? 1.5;
      ctx.lineJoin = 'round';
      ctx.setLineDash(s.style === 'dash' ? [4, 3] : []);
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < d.x.length; i++) {
        const yv = d.y[i]!;
        if (!Number.isFinite(yv)) {
          pen = false;
          continue;
        }
        const xx = X(d.x[i]!);
        const yy = Y(yv);
        if (pen) ctx.lineTo(xx, yy);
        else ctx.moveTo(xx, yy);
        pen = true;
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }
  // x axis ticks along the bottom; a second clock's along the top of the lanes, clear of the event lines.
  const ticks = model.x.ticks ?? (model.x.log ? logTicks(x0, x1) : niceTicks(x0, x1, Math.max(2, Math.round(px / 90))));
  drawAxis(ctx, L, model.x, ticks.map((t) => ({ at: t, text: model.x.format(t) })), L.h - PAD.bottom + 5, 'top', []);
  if (model.x2) {
    const c = model.x2;
    const lines = (model.events ?? []).map((e) => X(e.x));
    drawAxis(ctx, L, c, clockTicks(c, [x0, x1], Math.max(2, Math.round(px / 90))).map((v) => ({ at: v - c.shift, text: c.format(v) })), L.plotTop - 4, 'alphabetic', lines);
  }
  // Event lines reach up to their label row (through a second clock's); with the first lane's title on a row of its own they start at the plot.
  const lineTop = L.lineTop;
  for (const e of model.events ?? []) {
    const xx = Math.round(X(e.x)) + 0.5;
    if (xx < L.x.range[0] || xx > L.x.range[1]) continue;
    ctx.strokeStyle = C.event;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(xx, lineTop);
    ctx.lineTo(xx, L.h - PAD.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  for (const m of model.markers ?? []) {
    const xx = Math.round(X(m.x)) + 0.5;
    if (xx < L.x.range[0] || xx > L.x.range[1]) continue;
    ctx.strokeStyle = m.colour;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xx, lineTop + 2);
    ctx.lineTo(xx, L.h - PAD.bottom);
    ctx.stroke();
  }
  ctx.font = FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  for (const n of L.notes) {
    ctx.fillStyle = n.colour;
    ctx.fillText(n.text, n.left, PAD.top - 5);
  }
  // Point labels: every dot first, then each name beside its dot, nudged down a row at a time until it's
  // clear of every dot and every name placed before (a binding chart's Ki points share one row).
  const points = [...(model.labels ?? [])]
    .sort((a, b) => a.x - b.x)
    .flatMap((lb) => {
      const lane = L.lanes[lb.lane];
      const xx = scale(L.x, lb.x);
      return lane && xx >= L.x.range[0] && xx <= L.x.range[1] ? [{ lb, xx, yy: scale(lane.y, lb.y) }] : [];
    });
  for (const { lb, xx, yy } of points) {
    ctx.fillStyle = lb.colour;
    ctx.beginPath();
    ctx.arc(xx, yy, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.font = FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const boxes: { x: number; y: number; w: number }[] = points.map(({ xx, yy }) => ({ x: xx - 4, y: yy, w: 8 }));
  points.forEach(({ lb, xx, yy }, i) => {
    const tw = ctx.measureText(lb.text).width;
    const tx = xx + 7 + tw > L.x.range[1] ? xx - 7 - tw : xx + 7;
    const hit = (y: number) => boxes.find((b, j) => j !== i && Math.abs(b.y - y) < 13 && tx < b.x + b.w + 4 && tx + tw + 4 > b.x);
    let y = yy;
    for (let b = hit(y); b; b = hit(y)) y = Math.max(y, b.y) + 13;
    boxes.push({ x: tx, y, w: tw });
    ctx.fillStyle = lb.colour;
    haloText(ctx, lb.text, tx, y);
  });
  if (model.scaleBar && L.lanes[0]) {
    const lane = L.lanes[0];
    const hpx = Math.abs(scale(lane.y, model.scaleBar.value) - scale(lane.y, 0));
    const xx = L.w - PAD.right - 50.5;
    const yb = L.h - PAD.bottom - 6;
    ctx.strokeStyle = C.text;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xx, yb);
    ctx.lineTo(xx, yb - hpx);
    ctx.stroke();
    ctx.font = MONO;
    ctx.fillStyle = C.text;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(model.scaleBar.label, xx + 4, yb - hpx / 2);
  }
}

/**
 * One clock's tick labels in a row at `y` (ticks at data x `at`), its name in the gutter in the colour
 * of what it times, and its title at the row's right end. Labels that would sit on the title, or on
 * an event line (`avoid`, px), are left out.
 */
function drawAxis(ctx: CanvasRenderingContext2D, L: Layout, axis: XAxis, ticks: { at: number; text: string }[], y: number, baseline: CanvasTextBaseline, avoid: number[]) {
  const [lo, hi] = L.x.range;
  ctx.textBaseline = baseline;
  ctx.font = FONT;
  const titleW = axis.title ? ctx.measureText(axis.title).width : 0;
  if (axis.title) {
    ctx.fillStyle = C.faint;
    ctx.textAlign = 'right';
    ctx.fillText(axis.title, hi, y);
  }
  if (axis.name) {
    ctx.fillStyle = axis.name.colour;
    ctx.textAlign = 'right';
    ctx.fillText(axis.name.text, L.left - 8, y);
  }
  ctx.font = MONO;
  ctx.fillStyle = C.faint;
  ctx.textAlign = 'center';
  for (const t of ticks) {
    const xx = scale(L.x, t.at);
    if (xx < lo - 0.5 || xx > hi + 0.5) continue;
    const cx = Math.min(hi - 18, Math.max(lo + 18, xx));
    const half = ctx.measureText(t.text).width / 2;
    if (titleW && cx + half > hi - titleW - 10) continue;
    if (avoid.some((a) => Math.abs(a - cx) < half + 3)) continue;
    ctx.fillText(t.text, cx, y);
  }
}

function drawOverlay(ctx: CanvasRenderingContext2D, L: Layout, cursorX: number | null, hoverX: number | null) {
  ctx.clearRect(0, 0, L.w, L.h);
  const line = (x: number, colour: string, width: number) => {
    const xx = Math.round(scale(L.x, x)) + 0.5;
    if (xx < L.x.range[0] - 1 || xx > L.x.range[1] + 1) return;
    // Broken where it would cross a lane's title, which sits in the gap above its lane.
    ctx.font = FONT;
    const gaps = L.lanes.slice(1).flatMap(({ top, lane }) => {
      const title = laneTitle(lane);
      return title && xx >= L.left - 3 && xx <= L.left + ctx.measureText(title).width + 3 ? [[top - 16, top - 1] as const] : [];
    });
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.beginPath();
    let from = L.plotTop - 2;
    for (const [a, b] of gaps) {
      if (a > from) {
        ctx.moveTo(xx, from);
        ctx.lineTo(xx, a);
      }
      from = Math.max(from, b);
    }
    ctx.moveTo(xx, from);
    ctx.lineTo(xx, L.h - PAD.bottom);
    ctx.stroke();
  };
  if (cursorX !== null) line(cursorX, C.cursor, 1.5);
  if (hoverX !== null) {
    line(hoverX, 'rgba(255,255,255,0.35)', 1);
    for (const { y, lane } of L.lanes)
      for (const s of lane.series) {
        if (s.style === 'area' || s.readout === false) continue;
        const v = interp(s, hoverX);
        if (!Number.isFinite(v)) continue;
        ctx.fillStyle = s.colour;
        ctx.beginPath();
        ctx.arc(scale(L.x, hoverX), scale(y, v), 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
  }
}

function interp(s: Series, x: number, ys: ArrayLike<number> = s.y): number {
  const xs = s.x;
  const n = xs.length;
  if (!n || x < xs[0]! || x > xs[n - 1]!) return NaN;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid]! <= x) lo = mid;
    else hi = mid;
  }
  const w = xs[hi]! > xs[lo]! ? (x - xs[lo]!) / (xs[hi]! - xs[lo]!) : 0;
  return ys[lo]! + (ys[hi]! - ys[lo]!) * w;
}

export function Chart({ model, height, cursor, window: win, onSeek, label }: ChartProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const base = useRef<HTMLCanvasElement>(null);
  const over = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<{ x: number; px: number; py: number } | null>(null);
  const state = useRef<{ w: number; dpr: number; domain: [number, number]; layout: Layout | null; hoverX: number | null; repaint: (() => void) | null }>({
    w: 0,
    dpr: 1,
    domain: model.x.domain,
    layout: null,
    hoverX: null,
    repaint: null,
  });

  useEffect(() => {
    const el = wrap.current;
    const b = base.current;
    const o = over.current;
    if (!el || !b || !o) return;
    const bctx = b.getContext('2d');
    const octx = o.getContext('2d');
    if (!bctx || !octx) return;
    const st = state.current;
    let raf = 0;
    const paint = (force: boolean) => {
      const t = useAtlas.getState().time;
      const domain = win ? win(t) : model.x.domain;
      const moved = domain[0] !== st.domain[0] || domain[1] !== st.domain[1];
      if (force || moved || !st.layout) {
        st.domain = domain;
        st.layout = layout(bctx, model, st.w, height, domain);
        bctx.setTransform(st.dpr, 0, 0, st.dpr, 0, 0);
        drawBase(bctx, model, st.layout);
      }
      octx.setTransform(st.dpr, 0, 0, st.dpr, 0, 0);
      drawOverlay(octx, st.layout, cursor ? cursor(t) : null, st.hoverX);
    };
    const resize = () => {
      const w = Math.max(1, el.clientWidth);
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      st.w = w;
      st.dpr = dpr;
      for (const c of [b, o]) {
        c.width = Math.round(w * dpr);
        c.height = Math.round(height * dpr);
        c.style.width = `${w}px`;
        c.style.height = `${height}px`;
      }
      paint(true);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();
    const off = useAtlas.subscribe(
      (s) => s.time,
      () => {
        if (!raf)
          raf = requestAnimationFrame(() => {
            raf = 0;
            paint(false);
          });
      },
    );
    st.repaint = () => paint(false);
    return () => {
      st.repaint = null;
      ro.disconnect();
      off();
      cancelAnimationFrame(raf);
    };
  }, [model, height, cursor, win]);

  const toX = (clientX: number) => {
    const L = state.current.layout;
    const r = over.current?.getBoundingClientRect();
    if (!L || !r) return null;
    const px = clientX - r.left;
    if (px < L.x.range[0] || px > L.x.range[1]) return null;
    return invert(L.x, px);
  };
  const lanes = state.current.layout?.lanes;
  const under = model.readout === 'lane' && hover && lanes ? lanes.findIndex((l) => hover.py >= l.top - 2 && hover.py <= l.bottom + 2) : -1;
  const readout = hover
    ? model.lanes.flatMap((lane, i) =>
        (model.readout === 'lane' && i !== under ? [] : lane.series)
          .filter((s) => s.readout !== false && (s.style !== 'area' || s.readoutY))
          .map((s) => ({ s, v: interp(s, hover.x, s.readoutY ?? s.y), unit: s.unit ?? lane.unit ?? '' }))
          .filter((r) => Number.isFinite(r.v)),
      )
    : [];
  const setHoverX = (x: number | null) => {
    state.current.hoverX = x;
    state.current.repaint?.();
  };

  return (
    <div
      ref={wrap}
      className="chart"
      style={{ height }}
      role="img"
      aria-label={label}
      onPointerMove={(e) => {
        const x = toX(e.clientX);
        setHoverX(x);
        const r = wrap.current?.getBoundingClientRect();
        setHover(x === null || !r ? null : { x, px: e.clientX - r.left, py: e.clientY - r.top });
        if (onSeek && x !== null && e.buttons & 1) onSeek(x);
      }}
      onPointerDown={(e) => {
        const x = toX(e.clientX);
        if (onSeek && x !== null) {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          onSeek(x);
        }
      }}
      onPointerLeave={() => {
        setHoverX(null);
        setHover(null);
      }}
    >
      <canvas ref={base} className="chart-layer" />
      <canvas ref={over} className="chart-layer" style={{ cursor: onSeek ? 'pointer' : 'crosshair' }} />
      {hover && readout.length ? (
        <div className="chart-readout" style={{ left: hover.px > (wrap.current?.clientWidth ?? 0) - 200 ? hover.px - 12 : hover.px + 12, top: 4, transform: hover.px > (wrap.current?.clientWidth ?? 0) - 200 ? 'translateX(-100%)' : undefined }}>
          <div className="chart-readout-x num">{readoutX(model.x, model.x2, hover.x)}</div>
          {readout.slice(0, 8).map(({ s, v, unit }) => (
            <div key={s.label} className="chart-readout-row">
              <span className="swatch" style={{ background: s.colour }} />
              <span className="chart-readout-label">{s.label}</span>
              <span className="num">{`${formatValue(v)}${unit ? ` ${unit}` : ''}`}</span>
            </div>
          ))}
          {readout.length > 8 ? <div className="muted">{`and ${readout.length - 8} more`}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
